<script setup>
import { computed, ref, watch } from "vue";
import { Calendar } from "@element-plus/icons-vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  call: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "submit"]);

const year = ref("");
const label = ref("");
const dateRange = ref([]);
const active = ref(true);
const formError = ref("");
// 2026-09-29: una convocatoria nunca puede quedarse sin convenio (regla dura
// del backend, DELETE .../agreement rechaza el último) — por eso al CREAR
// una convocatoria nueva el convenio pasa a ser obligatorio en el mismo
// formulario. Al editar una ya existente no se pide (ya tiene al menos uno).
const agreementFile = ref(null);

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const isEditing = computed(() => !!props.call);

watch(() => props.modelValue, (isVisible) => {
  if (!isVisible) return;
  formError.value = "";
  agreementFile.value = null;
  if (props.call) {
    year.value = props.call.year;
    label.value = props.call.label || "";
    dateRange.value = [props.call.startDateRaw, props.call.endDateRaw];
    active.value = props.call.active;
  } else {
    const currentYear = String(new Date().getFullYear());
    year.value = currentYear;
    label.value = "";
    dateRange.value = [];
    active.value = true;
  }
});

const close = () => {
  if (props.saving) return;
  visible.value = false;
};

const onAgreementFileSelected = (event) => {
  agreementFile.value = event.target.files[0] || null;
};

const submit = () => {
  if (!year.value.trim()) {
    formError.value = "El año es obligatorio.";
    return;
  }
  if (!dateRange.value || dateRange.value.length !== 2) {
    formError.value = "Selecciona la fecha de apertura y de cierre.";
    return;
  }
  if (!isEditing.value && !agreementFile.value) {
    formError.value = "El convenio es obligatorio al crear una convocatoria.";
    return;
  }

  emit("submit", {
    year: year.value.trim(),
    label: label.value.trim() || null,
    startDate: `${dateRange.value[0]}T00:00:00`,
    endDate: `${dateRange.value[1]}T23:59:59`,
    active: active.value,
    agreementFile: agreementFile.value,
  });
};
</script>

<template>
  <el-dialog v-model="visible" width="560px" :show-close="false" :lock-scroll="true" class="social-fund-call-dialog">
    <section v-if="visible" class="social-fund-call-dialog__content">
      <img class="social-fund-call-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt="Cerrar"
        @click="close" />
      <header class="social-fund-call-dialog__header">
        <el-icon>
          <Calendar />
        </el-icon>
        <h2>{{ isEditing ? "Editar convocatoria" : "Nueva convocatoria" }}</h2>
      </header>

      <div class="form-item">
        <label for="call-year">Año</label>
        <el-input id="call-year" v-model="year" placeholder="2026" :disabled="saving" />
      </div>

      <div class="form-item">
        <label for="call-label">Etiqueta (opcional)</label>
        <el-input id="call-label" v-model="label" placeholder="Convocatoria 2026" :disabled="saving" />
      </div>

      <div class="form-item">
        <label for="call-dates">Apertura y cierre</label>
        <el-date-picker popper-class="social-fund-date-picker" id="call-dates" v-model="dateRange" type="daterange" class="w-100" format="DD/MM/YYYY"
          value-format="YYYY-MM-DD" range-separator="—" start-placeholder="Apertura" end-placeholder="Cierre"
          :disabled="saving" />
      </div>

      <div v-if="!isEditing" class="form-item">
        <label for="call-agreement">Convenio (PDF)</label>
        <label class="social-fund-call-dialog__file-label" :class="{ 'social-fund-call-dialog__file-label--disabled': saving }">
          {{ agreementFile ? agreementFile.name : "Seleccionar fichero..." }}
          <input id="call-agreement" type="file" accept="application/pdf" class="social-fund-call-dialog__file-input"
            :disabled="saving" @change="onAgreementFileSelected" />
        </label>
      </div>

      <el-checkbox v-model="active" :disabled="saving">Convocatoria activa</el-checkbox>

      <small v-if="formError" class="social-fund-call-dialog__error">{{ formError }}</small>

      <footer class="social-fund-call-dialog__actions">
        <button type="button" class="btn btn_yellow" :disabled="saving" @click="close">CANCELAR</button>
        <button type="button" class="btn btn_red" :disabled="saving" @click="submit">
          {{ saving ? "GUARDANDO..." : (isEditing ? "GUARDAR CAMBIOS" : "CREAR CONVOCATORIA") }}
        </button>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
:global(.el-dialog.social-fund-call-dialog) {
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.social-fund-call-dialog :deep(.el-dialog__header) {
  display: none;
}

.social-fund-call-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.social-fund-call-dialog__content {
  color: #263052;
  padding: 8px 16px 16px;
  position: relative;
}

.social-fund-call-dialog__close {
  height: 38px;
  object-fit: contain;
  position: absolute;
  right: 12px;
  top: 8px;
  width: 38px;
}

.social-fund-call-dialog__header {
  align-items: center;
  display: flex;
  gap: 12px;
  padding-right: 48px;
  margin-bottom: 18px;
}

.social-fund-call-dialog__header .el-icon {
  color: $bg-blue;
  font-size: 28px;
}

.social-fund-call-dialog__header h2 {
  color: $bg-blue;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -.05em;
  line-height: 1.1;
  margin: 0;
}

.social-fund-call-dialog__content .form-item label {
  color: #283150;
  display: block;
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 6px;
}

.social-fund-call-dialog__file-label {
  align-items: center;
  border: 1px solid #c5c8d5;
  border-radius: 8px;
  color: #283150;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  padding: 9px 12px;
  position: relative;
}

.social-fund-call-dialog__file-label--disabled {
  cursor: not-allowed;
  opacity: .5;
}

.social-fund-call-dialog__file-input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.social-fund-call-dialog__error {
  color: #d31c5e;
  display: block;
  font-size: 12px;
  margin-top: 10px;
}

.social-fund-call-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin: 20px -16px -16px;
  padding: 16px;
}

.social-fund-call-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  min-width: 160px;
}

.social-fund-call-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

.social-fund-call-dialog__actions .btn_red {
  color: #fff !important;
}

@media (max-width: 767px) {
  .social-fund-call-dialog__actions { flex-direction: column-reverse; }
  .social-fund-call-dialog__actions .btn { min-width: 0; width: 100%; }
  .social-fund-call-dialog__file-label { overflow-wrap: anywhere; }
}
</style>
